import { setupIonicReact } from '@ionic/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { PostHogProvider } from 'posthog-js/react';
import React, { lazy, Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';

import { environment } from '@env';

import {
  HOPRoutes,
  OnboardingRoutes,
  PMPRoutes,
  PublicRoutes,
  ResidentRoutes,
} from '@/config/routes';
import AuthProvider from '@/context/AuthProvider';
import OnboardingProgressProvider from '@/context/OnboardingProgressProvider';
import PropertyManagerProvider from '@/context/PropertyManagerProvider';
import { isAssociaSubdomain } from '@/utils/communities';

import { PendingReferralHandler } from '@/components/auth/PendingReferralHandler';
import { ReferralHandler } from '@/components/auth/ReferralHandler';
import ConditionalRoute from '@/components/ConditionalRoute';

import { queryClient } from './api/queryClient';

const AboutUsPage = lazy(() => import('@/pages/public/AboutUsPage'));
const SignUpPage = lazy(() => import('@/pages/public/auth/SignUpPage'));
const LogInPage = lazy(() => import('@/pages/public/auth/LogInPage'));
const LogOutPage = lazy(() => import('@/pages/public/auth/LogOutPage'));
const ResetPasswordPage = lazy(() => import('@/pages/public/auth/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('@/pages/public/auth/VerifyEmailPage'));
// const ChatPage = lazy(() => import('@/pages/public/ChatPage'));
// const HomePage = lazy(() => import('@/pages/public/HomePage'));
const PrivacyPolicyPage = lazy(() => import('@/pages/public/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('@/pages/public/TermsOfServicePage'));
const AssociaHomePage = lazy(() => import('@/pages/associa/AssociaHomePage'));
const LandingPage = lazy(() => import('@/pages/public/LandingPage'));
const NewLandingPage = lazy(() => import('@/pages/public/NewLandingPage'));
const PropertyManagementLandingPage = lazy(
  () => import('@/pages/public/PropertyManagementLandingPage')
);
const PricingPage = lazy(() => import('@/pages/public/PricingPage'));
const RentSuccessPage = lazy(() => import('@/pages/public/RentSuccessPage'));
const RentCancelPage = lazy(() => import('@/pages/public/RentCancelPage'));
const SharePage = lazy(() => import('@/pages/public/SharePage'));
const VendorSearchResultsPage = lazy(() => import('@/pages/public/VendorSearchResultsPage'));
const HOPDashboardPage = lazy(() => import('@/pages/home-owner-portal/DashboardPage'));
const HOPDocumentListPage = lazy(() => import('@/pages/home-owner-portal/DocumentListPage'));
const HOPDocumentViewPage = lazy(() => import('@/pages/home-owner-portal/DocumentViewPage'));
const HOPPropertyCreatePage = lazy(() => import('@/pages/home-owner-portal/PropertyCreatePage'));

const OnboardingHomePage = lazy(() => import('@/pages/onboarding/HomePage'));

const PMPHomePage = lazy(() => import('@/pages/property-manager-portal/HomePage'));
const PMPWorkflowsPage = lazy(() => import('@/pages/property-manager-portal/WorkflowPage'));

const ResidentHomePage = lazy(() => import('@/pages/resident/HomePage'));

const options = {
  api_host: environment.posthogUrl,
};

// Set up Ionic React with CSS disabled to avoid stylesheet conflicts
setupIonicReact({
  mode: 'md',
  animated: false, // Disable animations
});

// Add custom style to disable Ionic CSS
const disableIonicStyles = document.createElement('style');
disableIonicStyles.textContent = `
  /* Hide Ionic CSS Custom Properties */
  :root {
    --ion-color-primary: none !important;
    --ion-color-secondary: none !important;
    --ion-color-tertiary: none !important;
    --ion-background-color: none !important;
    --ion-text-color: none !important;
    --ion-safe-area-top: 0 !important;
    --ion-safe-area-right: 0 !important;
    --ion-safe-area-bottom: 0 !important;
    --ion-safe-area-left: 0 !important;
  }
  
  /* Force body to have standard behavior */
  body {
    position: static !important;
    width: auto !important;
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: auto !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    overscroll-behavior-y: auto !important;
    -webkit-text-size-adjust: 100% !important;
    -webkit-user-select: auto !important;
    user-select: auto !important;
    
    max-width: 100% !important;
    max-height: 100% !important;
    transform: none !important;
    contain: none !important;
  }
  
  /* Reset Ionic container styles that might affect scrolling */
  ion-app, ion-router-outlet, ion-content, .ion-page, .ion-page-invisible, .ion-page-hidden {
    position: static !important;
    width: auto !important;
    height: auto !important;
    contain: none !important;
    overflow: visible !important;
    z-index: auto !important;
    transform: none !important;
    background: transparent !important;
    color: inherit !important;
  }
  
  /* Ensure html has standard behavior */
  html {
    width: 100% !important;
    height: 100% !important;
    overflow: auto !important;
  }
  
  /* Ensure the root div can scroll */
  #root {
    height: 100% !important;
    overflow: auto !important;
  }
`;
document.head.appendChild(disableIonicStyles);

// Component with the application routes
const AppContent: React.FC = () => {
  React.useEffect(() => {
    // Clean up any stale redirect URLs that aren't share links
    const shareRedirectUrl = localStorage.getItem('shareRedirectUrl');
    const currentPath = window.location.pathname;

    // Remove shareRedirectUrl if:
    // 1. It's not a share link
    // 2. We're already on a share page (to prevent redirect loops)
    if (
      shareRedirectUrl &&
      (!shareRedirectUrl.includes('/share/') || currentPath.includes('/share/'))
    ) {
      localStorage.removeItem('shareRedirectUrl');
    }
  }, []);

  return (
    <>
      <ReferralHandler />
      <PendingReferralHandler />
      <Switch>
        <Route
          exact
          path={PublicRoutes.home}
          component={isAssociaSubdomain() ? AssociaHomePage : PropertyManagementLandingPage}
        />

        <Route exact path="/old" component={LandingPage} />

        <Route exact path={PublicRoutes.propertyManagement} component={NewLandingPage} />

        <Route exact path={PublicRoutes.login} component={LogInPage} />
        <Route exact path={PublicRoutes.signup} component={SignUpPage} />
        <Route exact path={PublicRoutes.logout} component={LogOutPage} />
        <Route exact path={PublicRoutes.resetPassword} component={ResetPasswordPage} />

        <Route exact path={PublicRoutes.privacyPolicy} component={PrivacyPolicyPage} />
        <Route exact path={PublicRoutes.termsOfService} component={TermsOfServicePage} />
        <Route exact path={PublicRoutes.aboutUs} component={AboutUsPage} />
        <Route exact path={PublicRoutes.pricing} component={PricingPage} />
        <Route exact path={PublicRoutes.rentSuccess} component={RentSuccessPage} />
        <Route exact path={PublicRoutes.rentCancel} component={RentCancelPage} />
        <Route exact path={PublicRoutes.shareLink} component={SharePage} />
        <Route exact path={PublicRoutes.vendorSearchResults} component={VendorSearchResultsPage} />

        <Route exact path={PublicRoutes.verifyEmail} component={VerifyEmailPage} />

        {/* <ConditionalRoute exact path={PublicRoutes.chat} component={ChatPage} /> */}

        <ConditionalRoute exact path={HOPRoutes.dashboard} component={HOPDashboardPage} />
        <ConditionalRoute exact path={HOPRoutes.propertyList}>
          <Redirect to={HOPRoutes.dashboard} />
        </ConditionalRoute>
        <ConditionalRoute exact path={HOPRoutes.propertyCreate} component={HOPPropertyCreatePage} />
        <ConditionalRoute exact path={HOPRoutes.documentList} component={HOPDocumentListPage} />
        <ConditionalRoute exact path={HOPRoutes.documentView} component={HOPDocumentViewPage} />

        <ConditionalRoute path={OnboardingRoutes.index} component={OnboardingHomePage} />

        <ConditionalRoute path={PMPRoutes.dashboard} component={PMPHomePage} />
        <ConditionalRoute path={PMPRoutes.workflows} component={PMPWorkflowsPage} />

        <ConditionalRoute path={ResidentRoutes.dashboard} component={ResidentHomePage} />

        <Route>
          <Redirect to={PublicRoutes.home} />
        </Route>
      </Switch>
    </>
  );
};

const App: React.FC = () => (
  <HelmetProvider>
    <PostHogProvider apiKey={environment.posthogKey} options={options}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <PropertyManagerProvider>
              <OnboardingProgressProvider>
                <Suspense>
                  <AppContent />
                </Suspense>
              </OnboardingProgressProvider>
            </PropertyManagerProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </PostHogProvider>
  </HelmetProvider>
);

export default App;
